// src/components/BottomNav.js
//
// Barra de navegação fixa no rodapé — só no celular/tablet (ver App.js).
// Traz as três funções do dia a dia; o resto fica no card "Mais", que sobe
// de baixo para cima.
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  House,
  ClipboardCheck,
  Truck,
  LayoutGrid,
  History,
  Scale,
  PackagePlus,
  List,
  ChevronRight,
  X,
  CalendarCheck,
} from "lucide-react";

const ITEMS = [
  { to: "/dashboard", label: "Início", icon: House, match: ["/dashboard"] },
  { to: "/contar-estoque", label: "Contar", icon: ClipboardCheck, match: ["/contar-estoque", "/cadastrar-produto-para-contagem", "/lista-produtos-para-contagem", "/editar-produto-para-contagem"] },
  { to: "/receber-mercadorias", label: "Receber", icon: Truck, match: ["/receber-mercadorias"] },
];

/* Opções do card "Mais" */
const MAIS = [
  { to: "/contagem-mensal", label: "Contagem mensal", desc: "Valor do estoque em R$ (CMV)", icon: CalendarCheck, match: ["/contagem-mensal"] },
  { to: "/minhas-contagens", label: "Contagens", desc: "Histórico e listas de compra", icon: History, match: ["/minhas-contagens", "/lista-de-compra"] },
  { to: "/cotacoes", label: "Conferir cotações", desc: "Comparar preços dos fornecedores", icon: Scale, match: ["/cotacoes"] },
  { to: "/cadastrar-produto", label: "Cadastrar produto", desc: "Novo item", icon: PackagePlus, match: ["/cadastrar-produto"] },
  { to: "/lista-produtos", label: "Itens cadastrados", desc: "Produtos e categorias", icon: List, match: ["/lista-produtos", "/editar-produto"] },
];

function isActive(pathname, match) {
  return match.some((m) => pathname === m || pathname.startsWith(`${m}/`));
}

export default function BottomNav() {
  const { pathname } = useLocation();
  const [maisAberto, setMaisAberto] = useState(false);

  // fecha o card ao trocar de página e com Esc
  useEffect(() => setMaisAberto(false), [pathname]);
  useEffect(() => {
    if (!maisAberto) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMaisAberto(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [maisAberto]);

  const maisAtivo = MAIS.some((m) => isActive(pathname, m.match));

  return (
    <>
      {maisAberto && (
        <div className="bottom-sheet-overlay" onClick={() => setMaisAberto(false)}>
          <div
            id="bottom-sheet-mais"
            className="bottom-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Mais opções"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bottom-sheet-head">
              <span className="bottom-sheet-title">Mais opções</span>
              <button type="button" className="btn-icon" onClick={() => setMaisAberto(false)} aria-label="Fechar">
                <X />
              </button>
            </div>
            <ul className="bottom-sheet-list">
              {MAIS.map(({ to, label, desc, icon: Icon, match }) => {
                const active = isActive(pathname, match);
                return (
                  <li key={to}>
                    <Link
                      to={to}
                      className={`bottom-sheet-item ${active ? "is-active" : ""}`}
                      aria-current={active ? "page" : undefined}
                      onClick={() => setMaisAberto(false)}
                    >
                      <span className="bottom-sheet-icon" aria-hidden="true"><Icon /></span>
                      <span className="bottom-sheet-text">
                        <strong>{label}</strong>
                        <span>{desc}</span>
                      </span>
                      <ChevronRight aria-hidden="true" className="bottom-sheet-chev" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      <nav className="bottom-nav" aria-label="Navegação principal">
        {ITEMS.map(({ to, label, icon: Icon, match }) => {
          const active = !maisAberto && isActive(pathname, match);
          return (
            <Link
              key={to}
              to={to}
              className={`bottom-nav-item ${active ? "is-active" : ""}`}
              aria-current={active ? "page" : undefined}
            >
              <span className="bottom-nav-icon" aria-hidden="true"><Icon /></span>
              <span className="bottom-nav-label">{label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          className={`bottom-nav-item ${maisAberto || maisAtivo ? "is-active" : ""}`}
          onClick={() => setMaisAberto((v) => !v)}
          aria-expanded={maisAberto}
          aria-controls="bottom-sheet-mais"
        >
          <span className="bottom-nav-icon" aria-hidden="true"><LayoutGrid /></span>
          <span className="bottom-nav-label">Mais</span>
        </button>
      </nav>
    </>
  );
}
